import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import './AdminDatabase.css';

const AdminDatabase = () => {
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importStatus, setImportStatus] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const token = localStorage.getItem('adminToken');

    if (!token) {
        navigate('/admin');
        return null;
    }

    const handleAuthError = (res) => {
        if (res.status === 401) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUsername');
            navigate('/admin');
            return true;
        }
        return false;
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/db/export`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (handleAuthError(res)) return;
            if (!res.ok) throw new Error('Export failed');

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            a.download = `takumi_backup_${timestamp}.db`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            alert('Export failed: ' + err.message);
        } finally {
            setExporting(false);
        }
    };

    const handleFileSelect = (file) => {
        if (!file) return;
        if (!file.name.endsWith('.db')) {
            setImportStatus({ type: 'error', message: 'Please select a .db file' });
            return;
        }
        setSelectedFile(file);
        setImportStatus(null);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFileSelect(e.dataTransfer.files[0]);
    };

    const handleImport = async () => {
        if (!selectedFile) return;

        setImporting(true);
        setImportStatus(null);

        try {
            const arrayBuffer = await selectedFile.arrayBuffer();
            const res = await fetch(`${API_BASE_URL}/admin/db/import`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/octet-stream'
                },
                body: arrayBuffer
            });

            if (handleAuthError(res)) return;

            const data = await res.json();

            if (res.ok) {
                setImportStatus({ type: 'success', message: data.message });
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            } else {
                setImportStatus({ type: 'error', message: data.error || 'Import failed' });
            }
        } catch (err) {
            setImportStatus({ type: 'error', message: 'Import failed: ' + err.message });
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="admin-database">
            <div className="admin-db-header">
                <button className="back-btn" onClick={() => navigate('/admin/dashboard')}>
                    ← Back to Dashboard
                </button>
                <div className="admin-db-title">
                    <h1>Database Management</h1>
                    <p>Backup and restore the Takumi SQLite database</p>
                </div>
            </div>

            <div className="db-cards">
                <div className="db-card">
                    <div className="db-card-icon">⬇️</div>
                    <h2>Export / Backup</h2>
                    <p>Download a complete backup of the current database — all racquets, strings, videos, and admin users.</p>
                    <button
                        className="db-btn db-btn-export"
                        onClick={handleExport}
                        disabled={exporting}
                    >
                        {exporting ? 'Preparing download...' : 'Download Backup'}
                    </button>
                    <div className="db-hint">
                        Saved as <code>takumi_backup_[timestamp].db</code>
                    </div>
                </div>

                <div className="db-card">
                    <div className="db-card-icon">⬆️</div>
                    <h2>Import / Restore</h2>
                    <p>Restore from a backup file. The database will be replaced immediately — no restart needed.</p>

                    <div
                        className={`drop-zone${dragOver ? ' drag-over' : ''}${selectedFile ? ' has-file' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".db"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileSelect(e.target.files[0])}
                        />
                        {selectedFile ? (
                            <div className="file-selected">
                                <span className="file-icon">🗄️</span>
                                <span className="file-name">{selectedFile.name}</span>
                                <span className="file-size">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                            </div>
                        ) : (
                            <div className="drop-placeholder">
                                <span className="drop-icon">📁</span>
                                <span>Drop a .db file here or click to browse</span>
                            </div>
                        )}
                    </div>

                    {importStatus && (
                        <div className={`import-status ${importStatus.type}`}>
                            {importStatus.type === 'success' ? '✓' : '✗'} {importStatus.message}
                        </div>
                    )}

                    <button
                        className="db-btn db-btn-import"
                        onClick={handleImport}
                        disabled={!selectedFile || importing}
                    >
                        {importing ? 'Uploading...' : 'Restore Database'}
                    </button>

                    <div className="db-warning">
                        ⚠️ This will immediately replace all current data. Export a backup first if needed.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDatabase;
